import React, { Suspense } from "react";
import { Button, IconButton, Typography } from "@mui/material";
import menus, { MenuItemType } from "./menus";
import JumboVerticalNavbar from "@jumbo/components/JumboVerticalNavbar/JumboVerticalNavbar";
import { DrawerHeader } from "@jumbo/components/JumboLayout/style";
import JumboScrollbar from "@jumbo/components/JumboScrollbar";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import { SIDEBAR_STYLES, SIDEBAR_VIEWS } from "@jumbo/utils/constants/layout";

import Zoom from "@mui/material/Zoom";
import Div from "@jumbo/shared/Div";
import SidebarSkeleton from "./SidebarSkeleton";
import Humburger from "../../../../../images/humburgerO.svg";
import { useNavigate } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { hasPermission } from "app/hooks/usePermissions";

const appVersion = (require("../../../../../../package.json") as { version: string })
  .version;

const Sidebar = () => {
  const { setAuthToken } = useJumboAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    setAuthToken(null);
    navigate("/auth/login");

    const keysToRemove = [
      "authUser",
      "branch",
      "store",
      "storeId",
      "countryCode",
      "countryId",
    ];

    keysToRemove.forEach((key) => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
      }
    });
  };

  const filteredMenus = menus.filter((menu: MenuItemType) =>
    hasPermission(menu.keyName),
  );

  return (
    <Div sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <SidebarHeader />
      <Div sx={{ flex: 1, minHeight: 0 }}>
        <JumboScrollbar
          autoHide
          autoHideDuration={200}
          autoHideTimeout={500}
          style={{ height: "100%" }}
        >
          <Suspense
            fallback={
              <Div
                sx={{
                  display: "flex",
                  minWidth: 0,
                  alignItems: "center",
                  alignContent: "center",
                  px: 3,
                }}
              >
                <SidebarSkeleton />
              </Div>
            }
          >
            <JumboVerticalNavbar translate items={filteredMenus} />
          </Suspense>
        </JumboScrollbar>
      </Div>
      <SidebarFooter onLogout={onLogout} />
    </Div>
  );
};

const SidebarFooter = ({ onLogout }: { onLogout: () => void }) => {

  return (
    <Div sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
      <Div sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          onClick={onLogout}
          variant="contained"
          color="primary"
          size="small"
          sx={{ minWidth: 0, textTransform: "none" }}
        >
          Logout
        </Button>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", ml: "auto" }}
        >
          Version {appVersion} &#169; Ligthfields
        </Typography>
      </Div>
    </Div>
  );
};

const SidebarHeader = () => {
  const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();
  const { authUser } = useJumboAuth();

  return (
    <React.Fragment>
      {sidebarOptions?.style !== SIDEBAR_STYLES.CLIPPED_UNDER_HEADER && (
        <DrawerHeader sx={{ minHeight: 72, py: 1 }}>
          {sidebarOptions?.view !== SIDEBAR_VIEWS.MINI && (
            <Zoom in={sidebarOptions?.open}>
              <Div sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1 }}>
                <IconButton
                  edge="start"
                  color="inherit"
                  sx={{
                    ml:
                      sidebarOptions?.open &&
                      sidebarOptions.style !==
                        SIDEBAR_STYLES.CLIPPED_UNDER_HEADER
                        ? -1
                        : sidebarOptions.style ===
                            SIDEBAR_STYLES.CLIPPED_UNDER_HEADER
                          ? -2
                          : 0,
                    mr: 1,
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={() => setSidebarOptions({ open: false })}
                >
                  <img src={Humburger} alt="humburger" />
                </IconButton>

                <Div sx={{ textAlign: "left", minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      display: "block",
                      lineHeight: 1.2,
                      fontWeight: 700,
                      textTransform: "capitalize",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 180,
                    }}
                  >
                    {authUser?.data?.name || "-"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      lineHeight: 1.2,
                    }}
                  >
                    {authUser?.data?.privilege || "Admin"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 180,
                    }}
                  >
                    {authUser?.data?.email || "-"}
                  </Typography>
                </Div>
              </Div>
            </Zoom>
          )}
        </DrawerHeader>
      )}
    </React.Fragment>
  );
};

export default Sidebar;
